import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { AtSign, CheckCircle2, Flag, Send, EyeOff, SkipForward, ExternalLink } from "lucide-react";

// Build a bsky.app URL from a stored Bluesky AT-URI so she can click through
// and see the original post in full context before approving a reply.
function blueskyWebUrl(reply) {
  if (!reply || reply.platform !== "bluesky") return null;
  const uri = reply.native_uri || "";
  const handle = (reply.author_handle || "").replace(/^@/, "");
  if (!uri || !handle) return null;
  const m = uri.match(/\/app\.bsky\.feed\.post\/([a-z0-9]+)$/i);
  if (!m) return null;
  return `https://bsky.app/profile/${handle}/post/${m[1]}`;
}

const classBadge = (c) => {
  if (c === "ambiguous") return { label: "Flagged — may be craft critique, may be edged", color: "var(--elle-amber)" };
  if (c === "tier3") return { label: "Tier-3 — plot probe (deflection drafted)", color: "var(--elle-oxblood-bright)" };
  return { label: "Clean", color: "var(--elle-ink-dim)" };
};

export default function Inbox() {
  const [replies, setReplies] = useState([]);
  const [tab, setTab] = useState("pending");
  const [sim, setSim] = useState({ platform: "twitter", author_handle: "@test_reader", text: "" });
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});

  const load = async () => {
    const { data } = await api.get("/replies", { params: { status: tab } });
    setReplies(data);
  };
  useEffect(() => { load(); }, [tab]);

  const draft = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const { data } = await api.post("/replies/draft", { reply_id: id });
      setDrafts((d) => ({ ...d, [id]: data.draft }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Draft failed"); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const send = async (id) => {
    const content = drafts[id];
    if (!content) return toast.error("Draft a reply first.");
    try {
      await api.post(`/replies/${id}/send`, { content });
      toast.success("Reply sent.");
      setDrafts((d) => ({ ...d, [id]: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const ignore = async (id) => {
    await api.post(`/replies/${id}/ignore`);
    toast.success("Marked ignored.");
    load();
  };

  const simulate = async () => {
    if (!sim.text.trim()) return toast.error("Paste some text.");
    try {
      const { data } = await api.post("/replies/simulate", sim);
      toast.success(`Classified as "${data.classification}". ${data.stored ? "Stored." : "Blocked silently."}`);
      setSim({ ...sim, text: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulation failed"); }
  };

  return (
    <div data-testid="inbox-page">
      <div className="mb-8">
        <div className="overline">The inbox</div>
        <h1 className="font-display text-4xl mt-2">Filtered replies.</h1>
        <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
          Only craft-directed content reaches this page. Blocked messages are never shown — only counted.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-8" data-testid="inbox-tabs">
        <TabsList className="bg-transparent border rounded-sm"
                  style={{ borderColor: "var(--elle-border)" }}>
          <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-sm">Pending</TabsTrigger>
          <TabsTrigger value="answered" data-testid="tab-answered" className="rounded-sm">Answered</TabsTrigger>
          <TabsTrigger value="ignored" data-testid="tab-ignored" className="rounded-sm">Ignored</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-5">
        {replies.length === 0 && (
          <div className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-faint)" }}>
            Nothing {tab} at the moment.
          </div>
        )}
        {replies.map((r) => {
          const b = classBadge(r.classification);
          return (
            <article key={r.id} data-testid={`reply-${r.id}`}
                     className="elle-surface p-6 rounded-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-sm" style={{ color: "var(--elle-amber)" }}>
                    <AtSign size={14}/>
                    <span className="font-mono-elle">{r.author_handle}</span>
                    <span style={{ color: "var(--elle-ink-faint)" }}>· {r.platform}</span>
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.2em] font-semibold"
                       style={{ color: b.color }}>
                    {b.label}
                  </div>
                </div>
                <div className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                  {new Date(r.received_at).toLocaleString()}
                </div>
              </div>
              <blockquote className="mt-4 pl-4 border-l text-base leading-relaxed"
                          style={{ borderColor: "var(--elle-border-strong)" }}>
                {r.sanitised_preview}
              </blockquote>
              {(() => {
                const url = blueskyWebUrl(r);
                return url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer"
                     data-testid={`view-original-${r.id}`}
                     className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono-elle hover:text-[var(--elle-amber)] transition-colors"
                     style={{ color: "var(--elle-ink-dim)" }}>
                    <ExternalLink size={11}/>
                    View original post on Bluesky
                  </a>
                ) : null;
              })()}

              {r.status === "pending" ? (
                <div className="mt-5 space-y-3">
                  <Textarea data-testid={`draft-${r.id}`}
                            rows={3}
                            value={drafts[r.id] ?? r.proposed_deflection ?? ""}
                            onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value })}
                            placeholder="Your reply in Elle's voice…"/>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid={`draft-btn-${r.id}`} onClick={() => draft(r.id)}
                            disabled={busy[r.id]} className="btn-ghost rounded-sm">
                      <Flag size={14} className="mr-2"/> {busy[r.id] ? "Drafting…" : "Draft with Voice Agent"}
                    </Button>
                    <Button data-testid={`send-btn-${r.id}`} onClick={() => send(r.id)}
                            className="btn-amber rounded-sm">
                      <Send size={14} className="mr-2"/> Send reply
                    </Button>
                    <Button data-testid={`ignore-btn-${r.id}`} onClick={() => ignore(r.id)}
                            className="btn-ghost rounded-sm" variant="outline">
                      <SkipForward size={14} className="mr-2"/> Ignore
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 text-sm" style={{ color: "var(--elle-ink-dim)" }}>
                  {r.status === "answered" ? (
                    <>
                      <div className="overline mb-2"><CheckCircle2 size={12} className="inline mr-1"/> Your reply</div>
                      <div className="pl-4 border-l" style={{ borderColor: "var(--elle-amber)" }}>
                        {r.elle_response}
                      </div>
                    </>
                  ) : (
                    <span className="flex items-center gap-1"><EyeOff size={12}/> Ignored.</span>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {/* Simulator */}
      <div className="mt-16 elle-surface p-6 rounded-sm">
        <div className="overline">Simulator (demo)</div>
        <h3 className="font-display text-2xl mt-1">Test the abuse filter</h3>
        <p className="text-xs mt-1 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
          Paste any message as if it had arrived. The Abuse Filter classifies it in real time:
          clean/ambiguous/tier-3 land here; blocked content goes straight to the log (metadata only).
        </p>
        <div className="mt-4 grid md:grid-cols-[160px_1fr_1fr] gap-3">
          <select data-testid="sim-platform" value={sim.platform}
                  onChange={(e)=>setSim({...sim, platform: e.target.value})}
                  className="elle-surface-2 rounded-sm px-3 py-2 text-sm">
            {["twitter","instagram","facebook","tiktok","bluesky"].map(p => <option key={p}>{p}</option>)}
          </select>
          <Input data-testid="sim-handle" value={sim.author_handle}
                 onChange={(e)=>setSim({...sim, author_handle: e.target.value})}
                 placeholder="@anonymous"/>
          <div/>
        </div>
        <Textarea data-testid="sim-text" rows={3} className="mt-3"
                  value={sim.text}
                  onChange={(e)=>setSim({...sim, text: e.target.value})}
                  placeholder="e.g. “The second Drakis chapter drags — cut it by half.” or “I'm going to find you.”"/>
        <Button data-testid="sim-submit" onClick={simulate} className="btn-amber rounded-sm mt-3">
          Run through filter
        </Button>
      </div>
    </div>
  );
}
